import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Shield, Trash2, Mail, Loader2, CheckCircle } from 'lucide-react';

const TeamManagement = ({ inline = false }) => {
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newUser, setNewUser] = useState({ name: '', email: '', role: 'VIEWER', project_id: '' });
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchUsers();
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const res = await axios.get('http://localhost:4000/api/projects');
            setProjects(res.data);
        } catch (err) {
            console.error("Error cargando proyectos:", err);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await axios.get('http://localhost:4000/api/users');
            setUsers(res.data);
        } catch (err) {
            console.error("Error cargando usuarios:", err);
            setError("No se pudieron cargar los usuarios.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            setCreating(true);
            setError(null);
            const res = await axios.post('http://localhost:4000/api/users', newUser);
            setUsers([res.data, ...users]);
            setShowModal(false);
            setNewUser({ name: '', email: '', role: 'VIEWER', project_id: '' });
        } catch (err) {
            console.error("Error creando usuario:", err);
            setError(err.response?.data?.error || "Error al crear el usuario.");
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteUser = async (id, name) => {
        if (!window.confirm(`¿Estás seguro que deseas eliminar al usuario ${name}?`)) return;
        try {
            await axios.delete(`http://localhost:4000/api/users/${id}`);
            setUsers(users.filter(u => u.id !== id));
        } catch (err) {
            console.error("Error eliminando usuario:", err);
            alert("No se pudo eliminar al usuario.");
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return 'bg-purple-500/10 text-purple-400 border-purple-500/20 shadow-purple-900/20';
            case 'COMPANY_ADMIN': return 'bg-blue-500/10 text-blue-400 border-blue-500/20 shadow-blue-900/20';
            case 'PROJECT_MANAGER': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-emerald-900/20';
            case 'VIEWER': return 'bg-slate-500/10 text-theme-text-secondary border-slate-500/20';
            case 'DEMO_USER': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20 shadow-yellow-900/20';
            default: return 'bg-gray-500/10 text-theme-text-secondary border-gray-500/20';
        }
    };

    const getRoleName = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return 'Súper Admin';
            case 'COMPANY_ADMIN': return 'Admin. Empresa';
            case 'PROJECT_MANAGER': return 'Residente/Gerente';
            case 'VIEWER': return 'Lector/Cliente';
            case 'DEMO_USER': return 'Demo Read-Only';
            default: return role;
        }
    };

    return (
        <div className={inline ? "w-full" : "min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans selection:bg-blue-500/30"}>
            {/* Header */}
            {!inline ? (
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                    <div>
                        <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2">
                            Gestión de <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Equipo</span>
                        </h1>
                        <p className="text-theme-text-secondary">Administra los usuarios y permisos de tu organización.</p>
                    </div>
                    <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-theme-text-primary px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-blue-500/20 font-bold">
                        <Plus size={20} /> Nuevo Usuario
                    </button>
                </div>
            ) : (
                <div className="flex justify-between items-center mb-6 border-b border-theme-border pb-4">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-theme-text-primary">
                        <Users className="text-blue-500" /> Gestión de Equipo
                    </h2>
                    <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-theme-text-primary px-4 py-2 rounded-xl transition-all shadow-md shadow-blue-500/25 font-bold text-sm">
                        <Plus size={16} /> Invitar Usuario
                    </button>
                </div>
            )}

            <div className={inline ? "w-full bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl overflow-hidden shadow-2xl relative" : "max-w-7xl mx-auto bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl overflow-hidden shadow-2xl relative"}>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-6 border-b border-theme-border bg-theme-panel/50 text-xs font-bold text-theme-text-secondary uppercase tracking-wider">
                    <div className="col-span-5 md:col-span-3">Usuario</div>
                    <div className="col-span-4 md:col-span-3 text-left">Rol / Nivel</div>
                    <div className="hidden md:block col-span-3 text-left">Asignación</div>
                    <div className="hidden md:block col-span-2 text-left">Registro</div>
                    <div className="col-span-3 md:col-span-1 text-center">Acciones</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-theme-border">
                    {loading ? (
                        <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={32} /></div>
                    ) : users.length === 0 ? (
                        <div className="p-12 text-center text-theme-text-secondary">No hay usuarios registrados.</div>
                    ) : (
                        users.map((user) => (
                            <div key={user.id} className="grid grid-cols-12 gap-4 p-6 items-center hover: transition-colors relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/0 to-blue-500/0 group-hover:from-blue-500/5 group-hover:to-transparent transition-all pointer-events-none"></div>
                                <div className="col-span-5 md:col-span-3 flex items-center gap-4 relative z-10">
                                    <div className="bg-gradient-to-br from-blue-500/10 to-transparent w-10 h-10 rounded-full flex items-center justify-center border border-theme-border shrink-0">
                                        <Users size={18} className="text-blue-400" />
                                    </div>
                                    <div className="overflow-hidden">
                                        <p className="text-sm font-bold text-theme-text-primary truncate">{user.name}</p>
                                        <p className="text-xs text-theme-text-secondary truncate flex items-center gap-1 mt-0.5"><Mail size={10} /> {user.email}</p>
                                    </div>
                                </div>
                                <div className="col-span-4 md:col-span-3 flex items-center relative z-10">
                                    <span className={`px-2 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg shadow-inner border flex items-center gap-1.5 ${getRoleBadgeColor(user.role)}`}>
                                        <Shield size={12} /> {getRoleName(user.role)}
                                    </span>
                                </div>
                                <div className="hidden md:block col-span-3 text-xs font-semibold text-theme-text-primary relative z-10 truncate">
                                    {user.project_name ? <span className="flex items-center gap-1 text-blue-400"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span> {user.project_name}</span> : <span className="text-theme-text-secondary">Acceso Global</span>}
                                </div>
                                <div className="hidden md:block col-span-2 text-xs text-theme-text-secondary font-mono relative z-10">
                                    {new Date(user.created_at).toLocaleDateString()}
                                </div>
                                <div className="col-span-3 md:col-span-1 flex justify-center relative z-10">
                                    {user.role !== 'SUPER_ADMIN' && user.email !== 'admin@gcarso.com' && (
                                        <button onClick={() => handleDeleteUser(user.id, user.name)} className="p-2 hover:bg-red-500/10 text-theme-text-secondary hover:text-red-400 rounded-lg transition-colors border border-transparent hover:border-red-500/20" title="Eliminar Usuario">
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Modal Crear Usuario */}
            {showModal && (
                <div className="fixed inset-0 bg-theme-bg/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl w-full max-w-md overflow-hidden flex flex-col shadow-[0_0_50px_rgba(37,99,235,0.1)]">
                        <div className="p-6 border-b border-theme-border flex justify-between items-center bg-theme-panel">
                            <h2 className="text-xl font-bold text-theme-text-primary flex items-center gap-2">
                                <Users className="text-blue-500" /> Invitar Usuario
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-theme-text-secondary hover:text-theme-text-primary transition-colors">&times;</button>
                        </div>
                        {error && (<div className="bg-red-500/10 border-l-2 border-red-500 text-red-400 text-xs font-bold px-6 py-3">{error}</div>)}
                        <form id="createUserForm" onSubmit={handleCreateUser} className="p-6 space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">Nombre Completo</label>
                                <input type="text" required value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 focus:shadow-[0_0_15px_rgba(37,99,235,0.2)] transition-all" placeholder="Ej. Juan Pérez" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">Correo Electrónico</label>
                                <input type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 focus:shadow-[0_0_15px_rgba(37,99,235,0.2)] transition-all" placeholder="correo@empresa.com" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">Nivel de Acceso (Rol)</label>
                                <div className="relative">
                                    <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 focus:shadow-[0_0_15px_rgba(37,99,235,0.2)] transition-all appearance-none cursor-pointer">
                                        <option value="VIEWER">Lector/Cliente (Métricas y Reportes)</option>
                                        <option value="PROJECT_MANAGER">Residente de Obra (Toma de acciones)</option>
                                        <option value="COMPANY_ADMIN">Administrador (Gestión de usuarios)</option>
                                        <option value="DEMO_USER">Usuario Demo (Solo lectura avanzada)</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-theme-text-secondary">&#9662;</div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">Asignar a Obra (Opcional)</label>
                                <div className="relative">
                                    <select value={newUser.project_id || ''} onChange={(e) => setNewUser({ ...newUser, project_id: e.target.value })} className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 focus:shadow-[0_0_15px_rgba(37,99,235,0.2)] transition-all appearance-none cursor-pointer hover:border-blue-500/50">
                                        <option value="">-- Acceso Global a la Empresa --</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-theme-text-secondary">&#9662;</div>
                                </div>
                            </div>
                        </form>
                        <div className="p-6 border-t border-theme-border bg-theme-panel flex justify-end gap-3">
                            <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl font-bold text-theme-text-secondary hover:text-theme-text-primary transition-colors">Cancelar</button>
                            <button type="submit" form="createUserForm" disabled={creating} className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-theme-text-primary px-6 py-2.5 rounded-xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 font-bold flex items-center gap-2">
                                {creating ? <Loader2 size={16} className="animate-spin" /> : <><CheckCircle size={16} /> Crear Cuenta</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamManagement;
